/**
 * TanStack Query hooks for the design conditions API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchDesignConditions,
  upsertDesignCondition,
  importDesignConditions,
  deleteDesignCondition,
  approveDesignCondition,
  rejectDesignCondition,
  type DesignCondition,
  type DesignConditionUpsert,
  type DesignConditionImportItem,
} from "../api/designConditions";

export function useDesignConditions(configurationId: string | null) {
  return useQuery<DesignCondition[]>({
    queryKey: ["design-conditions", configurationId],
    queryFn: () => fetchDesignConditions(configurationId!),
    enabled: !!configurationId,
  });
}

export function useUpsertDesignCondition(configurationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DesignConditionUpsert) => upsertDesignCondition(body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["design-conditions", configurationId] }),
  });
}

export function useImportDesignConditions(configurationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      conditions,
    }: {
      projectId: string;
      conditions: DesignConditionImportItem[];
    }) => importDesignConditions(configurationId!, projectId, conditions),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["design-conditions", configurationId] }),
  });
}

export function useDeleteDesignCondition(configurationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      deleteDesignCondition(id, reason),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["design-conditions", configurationId] }),
  });
}

export function useApproveDesignCondition(configurationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conditionId }: { conditionId: string }) =>
      approveDesignCondition(conditionId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["design-conditions", configurationId] }),
  });
}

export function useRejectDesignCondition(configurationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conditionId }: { conditionId: string }) =>
      rejectDesignCondition(conditionId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["design-conditions", configurationId] }),
  });
}
